import {
  BASE_ROOM_TYPE,
  OFFER_FILTER_TYPE,
  RATE_LAYOUT_TYPE,
  REVIEW_FILTER_TYPE_MAPPING,
  UBFF_REVIEW_FILTER_TYPE_MAPPING,
} from './constants';

import type { Maybe, Nullable, Stay } from '@shared/types/utils';

import {
  CLICK_AREA,
  CUG_TYPE,
  FILTER_TYPE,
} from '@shared/utils-frontend/mini-events/constants';
import { getStayField } from '@shared/utils-frontend/mini-events/get-stay-field';

export const buildAdditionalInfoPricesLoadedMessage = ({
  allRatesCount,
  correlationId,
  cugType,
  currency,
  dBook,
  dayviewCorrelationId,
  hasCoupon,
  hasDbook,
  hasMeta,
  hotelId,
  isCugDeal,
  isFiltered,
  layoutType,
  pageName,
  partnerId,
  price,
  ratesWithImageCount,
  requestId,
  roomTypes,
  searchId,
  stay,
}: {
  cugType?: string;
  allRatesCount: number;
  currency: string;
  dBook: boolean;
  hasCoupon: boolean;
  hasDbook: boolean;
  hasMeta?: boolean;
  hotelId: string;
  isCugDeal: boolean;
  pageName: string;
  partnerId: string;
  price: number;
  ratesWithImageCount: number;
  requestId?: string;
  searchId?: string;
  roomTypes?: string;
  layoutType?: string;
  isFiltered?: boolean;
  stay: Stay;
  correlationId?: string;
  dayviewCorrelationId?: string;
}) => ({
  prices_loaded: {
    has_cug_deal: isCugDeal,
    has_coupon: hasCoupon,
    has_dbook: hasDbook,
    has_meta: hasMeta,
    is_dbook: dBook,
    hotel_id: parseInt(hotelId, 10),
    partner_id: partnerId,
    price: { currency, amount: Math.round(price), unit: 'WHOLE' },
    search_id: searchId,
    page_name: pageName,
    request_id: requestId,
    room_types: roomTypes,
    is_filtered: isFiltered,
    layout_type: RATE_LAYOUT_TYPE[layoutType as string],
    all_rates_count: allRatesCount,
    rates_with_image_count: ratesWithImageCount,
    stay: getStayField(stay),
    correlation_id: correlationId,
    day_view_correlation_id: dayviewCorrelationId,
    cug_type: cugType ? CUG_TYPE[cugType.toUpperCase()] : undefined,
  },
});

export const buildAdditionalInfoNearbyMap = ({
  clickType,
  hotelId,
  isMapFullView,
  poiInfo,
}: {
  clickType: string;
  hotelId: number;
  isMapFullView: boolean;
  poiInfo: Nullable<{
    poi_id: number;
    distance_in_meters: Nullable<number>;
  }>;
}) => ({
  nearby_map: {
    is_map_full_view: isMapFullView,
    hotel_id: hotelId,
    click_type: clickType,
    poi_info: poiInfo,
  },
});

export const buildAdditionalInfoRecommendHotelsChipMessage = ({
  type,
}: {
  type: string;
}) => ({
  recommend_chip_info: {
    type: type ? type.toUpperCase() : 'UNSET_CHIP_TYPE',
  },
});

export const buildAdditionalInfoOfferFiltersClick = (
  filterValue: string[],
) => ({
  /*
  Currently we only support the 3 filters inside the mapping OFFER_FILTER_TYPE.
  We do not want to support mapping the keys from UBFF to our own keys,
  so we need to figure out how to do this before we can add more filters.
 */
  detail_rates_filter_click: Object.fromEntries(
    Object.entries(OFFER_FILTER_TYPE).map(([key, mappedKey]) => [
      mappedKey,
      filterValue.includes(key),
    ]),
  ),
});

export const buildAdditionalInfoBaseRoomTypeFilterSelected = (
  roomType: string,
  selected: boolean,
) => ({
  base_room_type_filter_info: {
    base_room_type:
      BASE_ROOM_TYPE[roomType] ||
      BASE_ROOM_TYPE.ROOM_FILTER_OPTION_ROOM_TYPE_UNSET_ROOM,
    is_selected: selected,
    bed_type: roomType, // for room type filters with prices experiment
  },
});

export const buildAdditionalInfoHotelPriceStatusMessage = ({
  hasRate,
  hotelId,
  isFiltered,
}: {
  hasRate: boolean;
  hotelId: string;
  isFiltered: boolean;
}) => ({
  hotel_prices_status: {
    hotel_id: parseInt(hotelId, 10),
    has_price: hasRate,
    is_filtered: isFiltered,
  },
});

export const buildAdditionalInfoReviewFilterClicked = ({
  type,
  value,
}: {
  type: string;
  value: Maybe<string>;
}) => ({
  review_filter_clicked: {
    type: REVIEW_FILTER_TYPE_MAPPING[type] ?? FILTER_TYPE.UNSET_FILTER_TYPE,
    value: value ?? '',
  },
});

export const buildUBFFReviewFilterClicked = ({
  type,
  value,
}: {
  type: string;
  value: Maybe<string>;
}) => ({
  review_filter_clicked: {
    type:
      UBFF_REVIEW_FILTER_TYPE_MAPPING[type] ?? FILTER_TYPE.UNSET_FILTER_TYPE,
    value: value ?? '',
  },
});

export const buildAdditionalInfoTranslationButtonClicked = ({
  clickArea,
  sourceLocale,
  targetLocale,
}: {
  clickArea: string;
  sourceLocale: string;
  targetLocale: string;
}) => ({
  translation_button_clicked: {
    click_area: CLICK_AREA[clickArea] ?? CLICK_AREA.UNSET_CLICK_AREA,
    source_locale: sourceLocale,
    target_locale: targetLocale,
  },
});

export const buildAdditionalInfoPaginationClicked = ({
  pageNum,
}: {
  pageNum: number;
}) => ({
  pagination_clicked: {
    page_num: pageNum,
  },
});

export const buildAdditionalInfoHotelMediaGalleryClicked = ({
  hotelMediaType,
}: {
  hotelMediaType: string;
}) => ({
  hotel_media_gallery_clicked: {
    hotel_media_type: hotelMediaType,
  },
});
